import {
  formatChildren,
  frameDocumentType,
  getBrandNameById,
  getCanonicalPagePath,
  getCurrentLang,
  getSelectedSite,
  OLD_CONTENT_TYPES,
  PROJECT_CATCH_ERROR_LIST,
} from "@platformx/utilities";
import graphqlInstance from "../../config/graphqlConfig";
import { consolidatePageModel } from "../../hooks/usePage/mapper";
import {
  UPDATE_CONTENT_BRANDS,
  PUBLISH_CONTENT_BRANDS,
  PUBLISH_SCHEMA_CONTENT_BRANDS,
  UPDATE_SCHEMA_CONTENT_BRANDS,
} from "../../graphQL/mutations/brandMutations";
import { schemaQueries } from "../../graphQL/queries/contentTypeQueries";
import { PageQueries } from "../../graphQL/queries/pageQueries";
import { FETCH_CONTENT_BY_PATH } from "../../graphQL/queries/contentTypesQueries";
import { buildShareWithBrandsInput, rebrandStructureData } from "./mapper";

export interface ShareWithBrandsRequest {
  path: string;
  contentType: string;
  defaultBrandId: string;
  selectedBrandIds: string[];
  schemaDocumentType?: string;
  pageState?: string;
  /** Document already fetched when the dialog opened — skips the re-fetch. */
  prefetchedDoc?: any;
  /** The item is live: publish again after the update so the new brand takes effect (like the editor's Publish). */
  publishAfter?: boolean;
}

export interface ShareWithBrandsResponse {
  message?: string;
  path?: string;
  brand_name: string;
  secondary_brandname: string[];
  /** Set only when publishAfter was requested. A failed publish never undoes the saved share. */
  published?: boolean;
  publishError?: string;
}

const isStandardContentType = (contentType: string): boolean =>
  OLD_CONTENT_TYPES.some((type) => type.toLowerCase() === contentType?.toLowerCase());

const errorMessage = (err: any): string =>
  err?.graphQLErrors?.[0]?.message || err?.message || "publish failed";

// Re-publish after the share, exactly as the editors' Publish does (update, then publish). The
// share is already saved at this point, so a failure is reported, not thrown.
const publishStandard = async (contentType: string, path: string) => {
  try {
    await graphqlInstance.mutate({
      mutation: PUBLISH_CONTENT_BRANDS,
      variables: { contentType, input: { page: path.substring(path.lastIndexOf("/") + 1) } },
      context: { skipBrandNameHeader: true },
    });
    return { published: true };
  } catch (err: any) {
    return { published: false, publishError: errorMessage(err) };
  }
};

const publishSchema = async (
  contentType: string,
  schemaDocumentType: string,
  documentName: string,
  documentId: string,
) => {
  try {
    await graphqlInstance.mutate({
      mutation: PUBLISH_SCHEMA_CONTENT_BRANDS,
      variables: {
        contentType,
        schema_document_type: schemaDocumentType,
        input: { document_name: documentName, document_id: documentId, status: "publish" },
      },
      context: { skipBrandNameHeader: true },
    });
    return { published: true };
  } catch (err: any) {
    return { published: false, publishError: errorMessage(err) };
  }
};

// Page docs are addressed by folder (path segment 6) + doc name, same as fetchPageModel.
const parsePagePath = (path: string) => {
  const segments = path?.split("/") ?? [];
  return { folder: segments[6], name: segments[segments.length - 1] };
};

const brandsApi = {
  /** Brand fields a page already carries — used to preselect the Share With Brands dialog. */
  getPageBrands: async (
    path: string,
  ): Promise<
    | { responseBrandname?: string; secondary_brandname?: string[]; canonical_url?: string }
    | undefined
  > => {
    try {
      const { folder, name } = parsePagePath(path);
      const { data } = await graphqlInstance.query({
        query: PageQueries.FETCH_PAGE_BRANDS,
        variables: { folder, path: name },
        context: { headers: { sitename: getSelectedSite() }, skipBrandNameHeader: true },
        fetchPolicy: "no-cache",
      });
      return data?.authoring_getCmsItemByPath;
    } catch (err: any) {
      // Non-fatal: the dialog still opens, just without preselection.
      console.error(`${PROJECT_CATCH_ERROR_LIST.authoring}:getPageBrands`, err);
      return undefined;
    }
  },

  sharePageWithBrands: async (
    path: string,
    defaultBrandId: string,
    selectedBrandIds: string[],
    username = "",
    publishAfter = false,
  ): Promise<ShareWithBrandsResponse> => {
    try {
      const { folder, name } = parsePagePath(path);
      const { data } = await graphqlInstance.query({
        query: PageQueries.FETCH_PAGE_MODEL_DRAFT,
        variables: { folder, path: name },
        context: { headers: { sitename: getSelectedSite() }, skipBrandNameHeader: true },
        fetchPolicy: "no-cache",
      });
      const fetched = data?.authoring_getCmsItemByPath;
      if (!fetched || Object.keys(fetched).length === 0) {
        throw new Error(`sharePageWithBrands: could not load page "${path}"`);
      }
      // updatePage replaces the whole model — rebuild it exactly as the editor does.
      const { children, content, PageSettings, ...rest } = fetched;
      delete rest.__typename;
      // Read-only brand fields: the share sets brand_name / secondary_brandname itself, and the
      // update input rejects responseBrandname / canonical_url.
      delete rest.responseBrandname;
      delete rest.canonical_url;
      delete rest.secondary_brandname;
      const prelemMetaArray = formatChildren(children || [], content || {});
      const model = consolidatePageModel(
        { ...rest, Children: prelemMetaArray },
        prelemMetaArray,
        { ...PageSettings },
        username,
      );
      // The page keeps copies of its canonical (PageSettings.PageURL and the auto JSON-LD url);
      // they must follow the new primary brand, or the old brand stays in both.
      const canonicalPath = rest.slug
        ? getCanonicalPagePath(
            rest.slug,
            getCurrentLang(),
            "sitepage",
            getBrandNameById(defaultBrandId),
          )
        : undefined;
      if (canonicalPath && model.PageSettings) {
        model.PageSettings = { ...model.PageSettings, PageURL: canonicalPath };
      }
      // consolidatePageModel replaced StructureData with the prelems' data; keep the page's stored
      // SEO structured data instead (only its auto url moves to the new brand).
      model.StructureData = rebrandStructureData(rest.StructureData, canonicalPath);
      const pageInput = {
        ...model,
        brand_name: defaultBrandId,
        secondary_brandname: selectedBrandIds,
      };
      const context = { headers: { sitename: getSelectedSite() }, skipBrandNameHeader: true };
      const { data: saved } = await graphqlInstance.mutate({
        mutation: PageQueries.SAVE_PAGE_MODEL,
        variables: { input: pageInput },
        context,
      });
      // A live page is published again with the model just saved — the editor's own publish call —
      // so the new brand takes effect on the site. The share is already saved: a failure here is
      // reported, never thrown.
      let publishResult: { published?: boolean; publishError?: string } = {};
      if (publishAfter) {
        try {
          await graphqlInstance.mutate({
            mutation: PageQueries.PUBLISH_PAGE_MODEL,
            variables: {
              input: {
                page: model.Page,
                parentpageurl: "/",
                currentpageurl: model.CurrentPageURL,
              },
              pageModelRequest: pageInput,
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
            context,
          });
          publishResult = { published: true };
        } catch (err: any) {
          publishResult = { published: false, publishError: errorMessage(err) };
        }
      }
      return {
        message: saved?.authoring_updatePage?.message,
        path,
        brand_name: defaultBrandId,
        secondary_brandname: selectedBrandIds,
        ...publishResult,
      };
    } catch (err: any) {
      console.error(`${PROJECT_CATCH_ERROR_LIST.authoring}:sharePageWithBrands`, err);
      throw err;
    }
  },

  shareWithBrands: async (request: ShareWithBrandsRequest): Promise<ShareWithBrandsResponse> => {
    try {
      if (isStandardContentType(request.contentType)) {
        // updateContent replaces the whole document — echo it back in full.
        let doc = request.prefetchedDoc;
        if (!doc || Object.keys(doc).length === 0) {
          const { data: fetched } = await graphqlInstance.query({
            query: FETCH_CONTENT_BY_PATH,
            variables: { contentType: request.contentType, path: request.path },
            context: { skipBrandNameHeader: true },
            fetchPolicy: "no-cache",
          });
          doc = fetched?.authoring_getCmsContentByPath;
        }
        if (!doc || Object.keys(doc).length === 0) {
          throw new Error(`shareWithBrands: could not load document "${request.path}"`);
        }

        const { data } = await graphqlInstance.mutate({
          mutation: UPDATE_CONTENT_BRANDS,
          variables: {
            contenttype: request.contentType,
            input: buildShareWithBrandsInput(
              request.contentType,
              doc,
              request.path,
              request.defaultBrandId,
              request.selectedBrandIds,
              request.pageState,
            ),
          },
          context: {
            skipBrandNameHeader: true,
          },
        });
        const result = data?.authoring_updateContent;
        return {
          message: result?.message,
          path: result?.path,
          brand_name: request.defaultBrandId,
          secondary_brandname: request.selectedBrandIds,
          ...(request.publishAfter
            ? await publishStandard(request.contentType, result?.path || request.path)
            : {}),
        };
      }

      // Filter the echoed document to the schema's declared fields.
      const [{ data: schemaDefFetched }, { data: schemaFetched }] = await Promise.all([
        graphqlInstance.query({
          query: schemaQueries.CONTENT_SCHEMA,
          variables: { filter: request.contentType },
          context: { skipBrandNameHeader: true },
          fetchPolicy: "no-cache",
        }),
        graphqlInstance.query({
          query: schemaQueries.CONTENT_SCHEMA_WITH_DATA,
          variables: {
            contentType: request.contentType,
            name: request.path,
            schema_document_type: request.schemaDocumentType || "contentType",
          },
          context: { skipBrandNameHeader: true },
          fetchPolicy: "no-cache",
        }),
      ]);
      const schemaDoc = schemaFetched?.authoring_getSchemaContent;
      if (!schemaDoc || Object.keys(schemaDoc).length === 0) {
        throw new Error(`shareWithBrands: could not load schema document "${request.path}"`);
      }
      const schemaFields: { name: string }[] = schemaDefFetched?.authoring_getSchema?.fields || [];
      if (schemaFields.length === 0) {
        throw new Error(`shareWithBrands: could not load schema for "${request.contentType}"`);
      }
      const filteredDoc = schemaFields.reduce<Record<string, any>>((acc, field) => {
        if (field?.name && schemaDoc[field.name] !== undefined) {
          acc[field.name] = schemaDoc[field.name];
        }
        return acc;
      }, {});

      // Brand fields are system fields on a schema-based document (the stored document returns
      // secondary_brandname / canonical_url although the schema never declares them), so they are
      // written regardless of the schema's declared fields — like standard content types.
      // Collections have no public URL, so they carry no canonical.
      const isCollection =
        frameDocumentType(request.schemaDocumentType || "contentType") === "collection";
      const canonicalUrl =
        !isCollection && schemaDoc?.slug
          ? getCanonicalPagePath(
              schemaDoc.slug,
              schemaDoc?.locale || getCurrentLang(),
              request.contentType,
              getBrandNameById(request.defaultBrandId),
            )
          : undefined;
      const { data } = await graphqlInstance.mutate({
        mutation: UPDATE_SCHEMA_CONTENT_BRANDS,
        variables: {
          contentType: request.contentType,
          schema_document_type: request.schemaDocumentType || "contentType",
          input: {
            ...filteredDoc,
            name: schemaDoc?.name || request.path,
            title: schemaDoc?.title || filteredDoc?.title,
            brand_name: request.defaultBrandId,
            secondary_brandname: request.selectedBrandIds,
            ...(canonicalUrl ? { canonical_url: canonicalUrl } : {}),
          },
        },
        context: {
          skipBrandNameHeader: true,
        },
      });
      const result = data?.authoring_updateSchemaContent;
      return {
        message: result?.message,
        path: request.path,
        brand_name: request.defaultBrandId,
        secondary_brandname: request.selectedBrandIds,
        ...(request.publishAfter
          ? await publishSchema(
              request.contentType,
              request.schemaDocumentType || "contentType",
              schemaDoc?.name || request.path,
              schemaDoc?.document_id,
            )
          : {}),
      };
    } catch (err: any) {
      console.error(`${PROJECT_CATCH_ERROR_LIST.authoring}:shareWithBrands`, err);
      throw err;
    }
  },
};

export default brandsApi;
